import { storage } from '@/lib/storage';
import { env } from '@/env';
import { authContract } from '@octave/contracts';
import { initClient, tsRestFetchApi } from '@ts-rest/core';
import { createLogger } from '../logger';
import { singleFlight } from '../singleFlight';

const log = createLogger('auth/refresh');

// Bare client: no auth interceptor, so a rejected refresh cannot recurse.
const bareAuthClient = initClient(authContract, {
  baseUrl: env.VITE_API_URL,
  api: tsRestFetchApi,
});

/**
 * Exchange the stored refresh token for fresh ones. Single-flight: concurrent
 * 401s share one refresh call. Returns false when no refresh token is stored
 * or the backend rejects it — the caller then logs the user out.
 */
export const refreshTokens = singleFlight(async (): Promise<boolean> => {
  const refreshToken = storage.get('refreshToken');
  if (!refreshToken) return false;
  try {
    const res = await bareAuthClient.refreshToken({ body: { refreshToken } });
    if (res.status !== 200) {
      log.error('Token refresh rejected by the backend');
      return false;
    }
    storage.set('accessToken', res.body.accessToken);
    storage.set('refreshToken', res.body.refreshToken);
    log.debug('Tokens refreshed');
    return true;
  } catch (error) {
    log.error('Token refresh failed', { error });
    return false;
  }
});

/**
 * Routes where a 401 is a legitimate business answer (bad password, expired
 * OTP, invalid reset token...), not an expired session: everything under
 * `/auth/` except `/auth/me`, the one authenticated route of the group.
 */
export function isPreAuthPath(path: string): boolean {
  return path.startsWith('/auth/') && path !== '/auth/me';
}

/** Session is unrecoverable: drop the tokens and send the user to login. */
export function logoutToLogin(): void {
  storage.remove('accessToken');
  storage.remove('refreshToken');
  window.location.href = '/login';
}
